import { useMemo, useState } from 'react'
import type { Rec } from '../../shared/schema'
import { createRecord, updateRecord, useRecords } from '../lib/api'
import { useSession } from '../lib/auth'
import { useEditor } from '../lib/useOpen'
import { fmtDay, fmtDayLong, today } from '../lib/format'
import { fillTemplate, TEMPLATES, type Template } from '../lib/templates'
import { RecordForm } from '../components/RecordForm'
import { PrintPreview } from '../components/Print'
import { Avatar, Badge, Button, Drawer, Empty, ErrorNote, Icon, Logo, PageHeader, Skeleton, useToast } from '../components/ui'

export function Documentos() {
  const { data, loading, error, refresh } = useRecords('documentos')
  const clientes = useRecords('clientes').data
  const props = useRecords('propiedades').data
  const ed = useEditor(data)
  const [gen, setGen] = useState<{ tpl: Template; doc?: Rec } | null>(null)
  const [print, setPrint] = useState<Rec | null>(null)
  const name = (id: string, list: Rec[], k = 'nombre') => list.find(x => x.id === id)?.[k]

  return (
    <div className="page">
      <PageHeader eyebrow="Módulo 4" title="Documentos y contratos" subtitle="Elegí una plantilla, completala con datos del CRM, revisala y exportala a PDF." />

      <div className="legal-note"><Icon name="shield" size={16} /><p><strong>Plantillas modelo.</strong> Antes de firmar con clientes, un escribano o abogado tiene que revisar que cumplan la normativa uruguaya aplicable a cada operación. Lo marcado como [completar] se completa a mano.</p></div>

      <section className="tpl-grid">
        {TEMPLATES.map(t => (
          <article key={t.key} className="tpl-card">
            <div className="tpl-paper"><span /><span /><span /><span /></div>
            <div>
              <h3>{t.titulo}</h3>
              <p>{t.descripcion}</p>
            </div>
            <Button variant="primary" size="sm" icon="plus" onClick={() => setGen({ tpl: t })}>Usar plantilla</Button>
          </article>
        ))}
      </section>

      <section className="panel">
        <header className="panel-head"><h2>Documentos guardados</h2><span className="muted">{data.length} en DB_Documentos</span></header>
        {error && <ErrorNote error={error} onRetry={refresh} />}
        {loading ? <Skeleton /> : data.length ? (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Documento</th><th>Tipo</th><th>Cliente</th><th>Propiedad</th><th>Fecha</th><th>Estado</th><th>Socia</th><th /></tr></thead>
              <tbody>
                {data.map(d => (
                  <tr key={d.id} onClick={() => ed.open(d)}>
                    <td><strong>{d.titulo}</strong><small className="block muted">{d.ref} · v{d.version || 1}</small></td>
                    <td>{d.tipo}</td>
                    <td>{name(d.cliente, clientes) ?? '—'}</td>
                    <td>{name(d.propiedad, props) ?? '—'}</td>
                    <td>{fmtDay(d.fecha)}</td>
                    <td><Badge>{d.estado}</Badge></td>
                    <td><Avatar name={d.socia} /></td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="row gap-xs">
                        {TEMPLATES.some(t => t.tipo === d.tipo) && <Button size="sm" variant="quiet" icon="edit" onClick={() => setGen({ tpl: TEMPLATES.find(t => t.tipo === d.tipo)!, doc: d })}>Editar texto</Button>}
                        <Button size="sm" variant="quiet" icon="printer" onClick={() => setPrint(d)}>PDF</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty title="Todavía no hay documentos" text="Usá una plantilla para crear el primero." />}
      </section>

      {gen && <Generator tpl={gen.tpl} doc={gen.doc} clientes={clientes} props={props} onClose={() => setGen(null)} onPrint={setPrint} />}
      <RecordForm col="documentos" open={ed.isOpen} record={ed.editing} defaults={ed.creating ?? undefined} onClose={ed.close}
        actions={rec => rec && <Button size="sm" variant="ghost" icon="printer" onClick={() => setPrint(rec)}>PDF</Button>} />
      <PrintPreview open={!!print} onClose={() => setPrint(null)} title={print?.titulo ?? 'Documento'}>
        {print && <DocSheet d={print} />}
      </PrintPreview>
    </div>
  )
}

function Generator({ tpl, doc, clientes, props, onClose, onPrint }: { tpl: Template; doc?: Rec; clientes: Rec[]; props: Rec[]; onClose: () => void; onPrint: (d: Rec) => void }) {
  const { name } = useSession()
  const toast = useToast()
  const [clienteId, setClienteId] = useState(doc?.cliente ?? '')
  const [propId, setPropId] = useState(doc?.propiedad ?? '')
  const [socia, setSocia] = useState(doc?.socia || name)
  const cliente = clientes.find(c => c.id === clienteId)
  const propiedad = props.find(p => p.id === propId)
  const auto = useMemo(() => fillTemplate(tpl.cuerpo, { cliente, propiedad, socia }), [tpl, cliente, propiedad, socia])
  const [text, setText] = useState(doc?.contenido && doc.contenido !== '(ejemplo)' ? doc.contenido : '')
  const [touched, setTouched] = useState(!!(doc?.contenido && doc.contenido !== '(ejemplo)'))
  const body = touched ? text : auto
  const pending = (body.match(/\[completar[^\]]*\]/g) ?? []).length
  const [busy, setBusy] = useState(false)

  async function save(estado: string) {
    setBusy(true)
    try {
      const titulo = `${tpl.tipo} · ${propiedad?.nombre ?? cliente?.nombre ?? 'sin asignar'}`
      const payload = { titulo, tipo: tpl.tipo, cliente: clienteId, propiedad: propId, socia, contenido: body, estado, fecha: today() }
      const saved = doc ? await updateRecord('documentos', doc, { ...payload, version: (Number(doc.version) || 1) + 1 }, { force: true })
        : await createRecord('documentos', { ...payload, version: 1 })
      toast(doc ? 'Nueva versión guardada en Notion' : 'Documento guardado en Notion')
      return saved
    } catch (e: any) { toast(e.message, 'error'); return null } finally { setBusy(false) }
  }

  return (
    <Drawer open onClose={onClose} wide eyebrow={doc ? `${doc.ref} · editar` : 'Nueva desde plantilla'} title={tpl.titulo}
      footer={<>
        <div className="foot-left"><span className={`pending-count ${pending ? 'warn' : 'ok'}`}>{pending ? `${pending} campos [completar]` : 'Sin campos pendientes'}</span></div>
        <div className="foot-right">
          <Button variant="quiet" onClick={onClose}>Cancelar</Button>
          <Button variant="ghost" icon="check" busy={busy} onClick={async () => { if (await save(pending ? 'Borrador' : 'En revisión')) onClose() }}>Guardar</Button>
          <Button variant="primary" icon="printer" busy={busy} onClick={async () => { const s = await save(pending ? 'Borrador' : 'Listo para firmar'); if (s) { onClose(); onPrint(s) } }}>Guardar y ver PDF</Button>
        </div>
      </>}>
      <div className="gen-grid">
        <div className="form-grid">
          <div className="field"><label htmlFor="g-cli">Cliente</label>
            <select id="g-cli" value={clienteId} onChange={e => setClienteId(e.target.value)}><option value="">— Elegir del CRM —</option>{clientes.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}</select></div>
          <div className="field"><label htmlFor="g-prop">Propiedad</label>
            <select id="g-prop" value={propId} onChange={e => setPropId(e.target.value)}><option value="">— Elegir propiedad —</option>{props.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}</select></div>
          <div className="field"><label htmlFor="g-socia">Socia que firma</label>
            <select id="g-socia" value={socia} onChange={e => setSocia(e.target.value)}><option>Leticia</option><option>Yesica</option></select></div>
        </div>
        <div className="field field-wide">
          <div className="row between"><label htmlFor="g-text">Texto del documento</label>
            {touched && <button type="button" className="link-btn" onClick={() => { setTouched(false); setText('') }}>Volver a autocompletar</button>}</div>
          <textarea id="g-text" className="doc-editor" value={body} onChange={e => { setTouched(true); setText(e.target.value) }} rows={24} />
          <small className="field-help">Los datos del cliente y la propiedad se completan solos. Si editás el texto, cambiar de cliente ya no lo reemplaza (podés volver a autocompletar).</small>
        </div>
      </div>
    </Drawer>
  )
}

function DocSheet({ d }: { d: Rec }) {
  return (
    <article className="sheet doc-sheet">
      <header className="sheet-head"><Logo size={22} /><span className="sheet-ref">{d.ref} · versión {d.version || 1} · {fmtDayLong(d.fecha || today())}</span></header>
      <pre className="doc-text">{d.contenido && d.contenido !== '(ejemplo)' ? d.contenido : 'Este documento de ejemplo no tiene texto. Abrilo con "Editar texto" para generarlo desde la plantilla.'}</pre>
      <footer className="sheet-foot"><span>REVELA · Paysandú, Uruguay</span><span>Modelo sujeto a revisión profesional</span></footer>
    </article>
  )
}
